import React, { useEffect, useState } from "react";
import { Badge, Button, Callout } from "@radix-ui/themes";
import { MagicWandIcon } from "@radix-ui/react-icons";
import { Pencil, Save } from "lucide-react";
import { useImprove } from "@/api/useImprove"; // simulator todo - replace
import { Textarea } from "@/components/ui/textarea";
import { Dialog } from "@/components/shared/dialog";
import { TrainingSessionEventType } from "@/v2/entities/training-session-event/TrainingSessionEvent.model";
import { ReleaseConversation } from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { useSimulator } from "../../context/SimulatorWrapper";
import { SimulatorChannel } from "@/v2/entities/simulator/Simulator.model";
import { useInstructionsNavigation } from "../../context/InstructionsNavigationContext";

const typeMapping = {
  instruction: "blue",
  brief: "green",
  prerequisite: "red",
};

const typeOfImprovement = ["instruction", "brief", "prerequisite"] as const;
type TypeOfImprovement = (typeof typeOfImprovement)[number];

function ImproveButton({
  handleIngestionOfContext,
  latestConversationIdInDB,
  topicIdRef,
  latestMessage,
}: {
  handleIngestionOfContext: (context: string, type: TypeOfImprovement) => void;
  latestConversationIdInDB: ReleaseConversation | null;
  topicIdRef: number | null;
  latestMessage: string | null;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [enhancement, setEnhancement] = useState<string | null>(null);
  const [originalInstructions, setOriginalInstructions] = useState("");
  const [type, setType] = useState<TypeOfImprovement | null>(null);
  const [instructions, setInstructions] = useState("");

  const [isEdit, setIsEdit] = useState(false);

  const { reportTrainingSessionEvent, simulator } = useSimulator();
  const { openImproveTab } = useInstructionsNavigation();
  const { mutate, isLoading } = useImprove();

  useEffect(() => {
    if (!isDialogOpen) {
      setEnhancement(null);
      setInstructions("");
      setOriginalInstructions("");
    }
  }, [isDialogOpen]);

  const handleSubmit = async () => {
    setOriginalInstructions(instructions);
    mutate(
      {
        message: instructions,
        useCase:
          simulator?.channel === SimulatorChannel.autonomous_quackchat
            ? "chat"
            : "ticket",
      },
      {
        onSuccess: (res) => {
          setEnhancement(res.object.enhancments);
          setType(res.object.typeOfImprovement);
          reportTrainingSessionEvent(
            TrainingSessionEventType.IMPROVE_SUGGESTED,
            {
              message: instructions,
              enhancement: res.object.enhancments,
              type: res.object.typeOfImprovement,
              messageImproved: latestMessage,
            },
            {},
            {
              topicId: topicIdRef ?? undefined,
              releaseConversationId: latestConversationIdInDB?.id ?? undefined,
            }
          );
        },
        onError: (error) => {
          console.error(error);
        },
      }
    );
  };

  const handleApprove = () => {
    if (enhancement && type) {
      handleIngestionOfContext(enhancement, type);
      reportTrainingSessionEvent(
        TrainingSessionEventType.IMPROVE_SUBMITTED,
        {
          message: instructions,
          enhancement: enhancement,
          type: type,
          messageImproved: latestMessage,
        },
        {},
        {
          topicId: topicIdRef ?? undefined,
          releaseConversationId: latestConversationIdInDB?.id ?? undefined,
        }
      );
    }
    setIsDialogOpen(false);
  };

  const handleUndo = () => {
    if (originalInstructions && type) {
      handleIngestionOfContext(originalInstructions, type);
      reportTrainingSessionEvent(
        TrainingSessionEventType.IMPROVE_SUBMITTED,
        {
          message: originalInstructions,
          enhancement: originalInstructions,
          type: type,
          messageImproved: latestMessage,
        },
        {},
        {
          topicId: topicIdRef ?? undefined,
          releaseConversationId: latestConversationIdInDB?.id ?? undefined,
        }
      );
    }
    setIsDialogOpen(false);
  };

  const handleImproveClick = React.useCallback(() => {
    openImproveTab();
    return;
  }, [openImproveTab]);

  const handleDialogChange = React.useCallback((open: boolean) => {
    setIsDialogOpen(open);
  }, []);

  return (
    <>
      <Button
        type="button"
        color="gray"
        variant="outline"
        className="gap-2"
        size="2"
        onClick={handleImproveClick}
      >
        <MagicWandIcon />
        Improve
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogChange}>
        <Dialog.Content>
          <Dialog.Title>Suggest an improvement</Dialog.Title>
          <div className="space-y-4 flex flex-col gap-2 p-6">
            {enhancement ? (
              <>
                <div className="flex flex-col gap-4">
                  <label
                    htmlFor="instructions"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Your suggestion has been used to enhance this answer. We've
                    updated it and routed your input to the right place.
                  </label>
                  {type && (
                    <Badge
                      color={typeMapping[type] as "blue" | "green" | "red"}
                      variant="soft"
                      className="w-fit"
                    >
                      {type}
                    </Badge>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Original
                      </label>
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-md h-32 overflow-y-auto text-sm text-gray-600">
                        {originalInstructions}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Enhanced
                      </label>
                      {isEdit ? (
                        <div className="flex relative">
                          <Textarea
                            id="enhancement"
                            value={enhancement}
                            onBlur={() => setIsEdit(false)}
                            onChange={(e) => setEnhancement(e.target.value)}
                            className="h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none w-full"
                            placeholder="Enter your improvement instructions here..."
                          />
                          <Button
                            color="gray"
                            variant="ghost"
                            size="1"
                            highContrast
                            className="p-2 rounded-md absolute right-0 bottom-0"
                            onClick={() => setIsEdit(false)}
                          >
                            <Save className="w-4 h-4 text-gray-500" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex relative">
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md h-32 overflow-y-auto text-sm w-full">
                            {enhancement}
                          </div>
                          <Button
                            color="gray"
                            variant="ghost"
                            size="1"
                            highContrast
                            className="p-2 rounded-md absolute right-0 bottom-0"
                            onClick={() => setIsEdit(true)}
                          >
                            <Pencil className="w-3 h-3 text-gray-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="instructions"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Help us enhance this answer. Share what could be
                    better—Quack will refine it and update where needed.
                  </label>

                  <Textarea
                    id="instructions"
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    className=" w-auto h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Enter your improvement instructions here..."
                  />
                </div>
              </>
            )}

            <Callout.Root color="blue" className="flex gap-2">
              <Callout.Icon className=" w-fit">💡</Callout.Icon>
              <Callout.Text className=" m-0 ">
                <b>Tip:</b> Your feedback guides how Quack responds here. Be
                specific—share any missing details, unclear explanations, or
                areas that could be better covered in future answers.
              </Callout.Text>
            </Callout.Root>
            <div className="flex justify-end space-x-2">
              <Button
                color="gray"
                variant="ghost"
                size="1"
                highContrast
                className="p-2 rounded-md"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              {enhancement ? (
                <>
                  <Button
                    color="gray"
                    variant="outline"
                    size="2"
                    className="p-2 rounded-md"
                    onClick={handleUndo}
                    disabled={!originalInstructions.trim()}
                  >
                    Use Original
                  </Button>
                  <Button
                    loading={isLoading}
                    color="gray"
                    variant="solid"
                    size="2"
                    highContrast
                    className="p-2 rounded-md"
                    onClick={handleApprove}
                    disabled={!instructions.trim()}
                  >
                    Use Enhanced
                  </Button>
                </>
              ) : (
                <Button
                  loading={isLoading}
                  color="gray"
                  variant="solid"
                  size="2"
                  highContrast
                  className="p-2 rounded-md"
                  onClick={handleSubmit}
                  disabled={!instructions.trim()}
                >
                  Improve
                </Button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}

export default ImproveButton;
